import { Alert, Button, Empty, Modal, Space, Spin, Tag, Typography } from "antd";
import { ArrowRightOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "../app/api.ts";
import { formatDate } from "../app/classes.ts";
import { lessonKeys, type LessonChangeDetails, type LessonChangeKind, type LessonDetails } from "../app/lessons.ts";
import { formatSchoolDateTime } from "../app/time.ts";
import { shortTime } from "../app/timetable.ts";

interface Props {
  classId: string;
  lesson: LessonDetails | undefined;
  onClose(): void;
}

const kindColors: Record<LessonChangeKind, string> = {
  Cancelled: "red",
  Moved: "blue",
  SubstituteAssigned: "purple",
  SubstituteRemoved: "default",
  Restored: "green",
};

/** The lesson's change log (GET /academic/lessons/{id}/changes), oldest first, with times in school time. */
export function LessonHistoryModal({ classId, lesson, onClose }: Props) {
  const { t } = useTranslation();

  const changes = useQuery({
    queryKey: lessonKeys.changes(classId, lesson?.id ?? ""),
    queryFn: async () => {
      const { data, error } = await api.GET("/academic/lessons/{id}/changes", { params: { path: { id: lesson?.id ?? "" } } });
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
    enabled: lesson !== undefined,
  });

  const position = (date: string, period: number | string, start: string, end: string) =>
    t("lessons.historyPosition", { date: formatDate(date), period: Number(period), start: shortTime(start), end: shortTime(end) });

  const beforeAfter = (label: string, before: string, after: string) => (
    <div>
      <Typography.Text type="secondary">{`${label}: `}</Typography.Text>
      {before === after ? (
        <Typography.Text>{after}</Typography.Text>
      ) : (
        <>
          <Typography.Text delete>{before}</Typography.Text>
          <ArrowRightOutlined aria-hidden style={{ marginInline: 6, fontSize: 11 }} />
          <Typography.Text>{after}</Typography.Text>
        </>
      )}
    </div>
  );

  const renderChange = (c: LessonChangeDetails) => (
    <li key={c.id} style={{ padding: "12px 0", borderBottom: "1px solid rgba(5, 5, 5, 0.06)" }}>
      <Space orientation="vertical" size={2} style={{ width: "100%" }}>
        <Space wrap size={8}>
          <Tag color={kindColors[c.kind]}>{t(`lessons.kinds.${c.kind}`)}</Tag>
          <Typography.Text type="secondary">{formatSchoolDateTime(c.occurredAt)}</Typography.Text>
        </Space>
        {beforeAfter(
          t("lessons.historyWhen"),
          position(c.oldDate, c.oldPeriod, c.oldStartTime, c.oldEndTime),
          position(c.newDate, c.newPeriod, c.newStartTime, c.newEndTime),
        )}
        {beforeAfter(t("lessons.historyTeacher"), c.oldTeacherName ?? t("lessons.noTeacher"), c.newTeacherName ?? t("lessons.noTeacher"))}
        {c.cancellationCause && (
          <div>
            <Typography.Text type="secondary">{`${t(c.kind === "Restored" ? "lessons.historyUndoneCause" : "lessons.historyCause")}: `}</Typography.Text>
            <Typography.Text>{`${t(`lessons.causes.${c.cancellationCause}`)} — ${c.cancellationReason ?? ""}`}</Typography.Text>
          </div>
        )}
        <div>
          <Typography.Text type="secondary">{`${t("lessons.historyActor")}: `}</Typography.Text>
          <Typography.Text>{c.actorUserId ? c.actorName : t("audit.system")}</Typography.Text>
        </div>
      </Space>
    </li>
  );

  return (
    <Modal
      open={lesson !== undefined}
      title={t("lessons.historyTitle")}
      onCancel={onClose}
      footer={<Button onClick={onClose}>{t("common.close")}</Button>}
      destroyOnHidden
    >
      {lesson && (
        <Typography.Paragraph type="secondary">
          {t("lessons.lessonLabel", { subject: lesson.subjectName, date: formatDate(lesson.date), period: Number(lesson.period) })}
        </Typography.Paragraph>
      )}
      {changes.isError && (
        <Alert type="error" showIcon title={t("app.error")} action={<Button onClick={() => void changes.refetch()}>{t("app.retry")}</Button>} />
      )}
      {changes.isPending && lesson !== undefined && <Spin style={{ display: "block", margin: "24px auto" }} />}
      {changes.data?.length === 0 && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("lessons.historyEmpty")} />}
      {changes.data && changes.data.length > 0 && <ol style={{ listStyle: "none", margin: 0, padding: 0 }}>{changes.data.map(renderChange)}</ol>}
    </Modal>
  );
}
